import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiZap,
  FiCheck,
  FiX,
  FiTrash2,
} from 'react-icons/fi';
import api from '../services/api';
import MarketingProposalComposer, {
  proposalUsesIgComposer,
} from './marketing/MarketingProposalComposer';
import '../components/Button.css';
import './MarketingKalender.css';

const SOURCE_LABELS = {
  editorial: 'Redactieplan',
  calendar: 'Kalender',
  season: 'Seizoen',
  stock: 'Voorraad',
  sales: 'Verkoop',
};

const CHANNEL_LABELS = {
  email: 'E-mail',
  meta: 'Meta',
  woo: 'WooCommerce',
  shopify: 'WooCommerce',
  other: 'Overig',
};

const PROPOSAL_FILTER_CHANNELS = [
  { key: 'meta', label: 'Meta' },
  { key: 'email', label: 'E-mail' },
  { key: 'woo', label: 'WooCommerce' },
  { key: 'other', label: 'Overig' },
];

const PROPOSAL_FILTER_SOURCES = [
  { key: 'editorial', label: 'Redactie' },
  { key: 'calendar', label: 'Kalender' },
  { key: 'stock', label: 'Voorraad' },
  { key: 'sales', label: 'Verkoop' },
  { key: 'season', label: 'Seizoen' },
];

const DEFAULT_PROPOSAL_CHANNEL_FILTERS = Object.fromEntries(
  PROPOSAL_FILTER_CHANNELS.map((c) => [c.key, true])
);
const DEFAULT_PROPOSAL_SOURCE_FILTERS = Object.fromEntries(
  PROPOSAL_FILTER_SOURCES.map((s) => [s.key, true])
);

function loadJsonStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed ? { ...fallback, ...parsed } : fallback;
  } catch {
    return fallback;
  }
}

function proposalChannelFilterKey(channel) {
  if (channel === 'shopify') return 'woo';
  if (PROPOSAL_FILTER_CHANNELS.some((c) => c.key === channel)) return channel;
  return 'other';
}

function monthStart(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function daysInMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

/** Toon event alleen op startdag, of op elke dag binnen starts_on–ends_on. */
function eventOnDay(ev, iso) {
  if (!ev?.starts_on) return false;
  if (ev.starts_on === iso) return true;
  if (!ev.ends_on) return false;
  return ev.starts_on < iso && ev.ends_on >= iso;
}

function parseIsoDate(iso) {
  const [y, m, d] = String(iso).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatIsoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDaysIso(iso, days) {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + days);
  return formatIsoDate(d);
}

function dayDiffIso(fromIso, toIso) {
  return Math.round((parseIsoDate(toIso) - parseIsoDate(fromIso)) / 86400000);
}

/** Verschuif start (en eind) met hetzelfde aantal dagen als de drop t.o.v. de drag-cel. */
function shiftEventDates(ev, fromIso, toIso) {
  const delta = dayDiffIso(fromIso, toIso);
  if (!delta || !ev?.starts_on) return null;
  return {
    starts_on: addDaysIso(ev.starts_on, delta),
    ends_on: ev.ends_on ? addDaysIso(ev.ends_on, delta) : null,
  };
}

function AiCopyBlock({ copy }) {
  if (!copy) return null;
  return (
    <div className="marketing-ai-copy">
      <h4>AI-copy &amp; ideeën</h4>
      {Array.isArray(copy.ideas) && copy.ideas.length > 0 && (
        <ul className="marketing-ideas-list">
          {copy.ideas.map((idea, i) => (
            <li key={i}>{idea}</li>
          ))}
        </ul>
      )}
      {copy.email_subject && (
        <div className="marketing-copy-block">
          <strong>E-mail onderwerp</strong>
          <p>{copy.email_subject}</p>
          {copy.email_preview && <p className="muted">{copy.email_preview}</p>}
          {copy.email_body && <div className="marketing-copy-html" dangerouslySetInnerHTML={{ __html: copy.email_body }} />}
        </div>
      )}
      {copy.meta_primary_text && (
        <div className="marketing-copy-block">
          <strong>Meta</strong>
          {copy.meta_headline && <p>{copy.meta_headline}</p>}
          <p>{copy.meta_primary_text}</p>
        </div>
      )}
      {(copy.woo_blog_title || copy.shopify_title) && (
        <div className="marketing-copy-block">
          <strong>WooCommerce (4shine.nl)</strong>
          <p>{copy.woo_blog_title || copy.shopify_title}</p>
          {(copy.woo_blog_body || copy.shopify_body) && (
            <pre className="marketing-copy-pre">{copy.woo_blog_body || copy.shopify_body}</pre>
          )}
        </div>
      )}
      {copy.woocommerce_coupon_idea && (
        <div className="marketing-copy-block">
          <strong>Coupon / actie</strong>
          <p>{copy.woocommerce_coupon_idea}</p>
        </div>
      )}
    </div>
  );
}

const MarketingKalender = () => {
  const [cursor, setCursor] = useState(() => monthStart(new Date()));
  const [proposals, setProposals] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [planDate, setPlanDate] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [eventNotes, setEventNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [igConfig, setIgConfig] = useState(null);
  const [eventIgImage, setEventIgImage] = useState('');
  const [eventIgImageMeta, setEventIgImageMeta] = useState('');
  const [igBusy, setIgBusy] = useState(false);
  const [igAutoForm, setIgAutoForm] = useState({
    enabled: false,
    default_publish_time: '10:00',
    max_posts_per_day: 2,
    timezone: 'Europe/Amsterdam',
  });
  const [planIgAuto, setPlanIgAuto] = useState(false);
  const [eventIgAuto, setEventIgAuto] = useState(false);
  const [eventIgPublishTime, setEventIgPublishTime] = useState('');
  const [dropHighlightIso, setDropHighlightIso] = useState(null);
  const [movingEventId, setMovingEventId] = useState(null);
  const [proposalChannelFilters, setProposalChannelFilters] = useState(() =>
    loadJsonStorage('marketingKalenderProposalChannels', DEFAULT_PROPOSAL_CHANNEL_FILTERS)
  );
  const [proposalSourceFilters, setProposalSourceFilters] = useState(() =>
    loadJsonStorage('marketingKalenderProposalSources', DEFAULT_PROPOSAL_SOURCE_FILTERS)
  );
  const dragRef = useRef({ eventId: null, fromIso: null, didMove: false });

  const range = useMemo(() => {
    const start = monthStart(cursor);
    const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    return {
      from: start.toISOString().slice(0, 10),
      to: end.toISOString().slice(0, 10),
    };
  }, [cursor]);

  const proposalsByDate = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return [...proposals]
      .filter((p) => !p.suggested_date || p.suggested_date >= today)
      .sort((a, b) => {
        const da = a.suggested_date || '9999-12-31';
        const db = b.suggested_date || '9999-12-31';
        if (da !== db) return da.localeCompare(db);
        const pa = a.priority ?? 50;
        const pb = b.priority ?? 50;
        if (pa !== pb) return pb - pa;
        return (a.id || 0) - (b.id || 0);
      });
  }, [proposals]);

  const proposalsFiltered = useMemo(() => {
    return proposalsByDate.filter((p) => {
      const chKey = proposalChannelFilterKey(p.channel);
      const srcKey = p.source || 'other';
      const channelOk = proposalChannelFilters[chKey] !== false;
      const sourceOk =
        PROPOSAL_FILTER_SOURCES.some((s) => s.key === srcKey)
          ? proposalSourceFilters[srcKey] !== false
          : proposalSourceFilters.other !== false;
      return channelOk && sourceOk;
    });
  }, [proposalsByDate, proposalChannelFilters, proposalSourceFilters]);

  const proposalDateGroups = useMemo(() => {
    const groups = [];
    let last = null;
    for (const p of proposalsFiltered) {
      const d = p.suggested_date || 'Zonder datum';
      if (d !== last) {
        groups.push({ date: d, items: [] });
        last = d;
      }
      groups[groups.length - 1].items.push(p);
    }
    return groups;
  }, [proposalsFiltered]);

  const proposalFiltersActive = useMemo(() => {
    const chOff = PROPOSAL_FILTER_CHANNELS.some((c) => !proposalChannelFilters[c.key]);
    const srcOff = PROPOSAL_FILTER_SOURCES.some((s) => !proposalSourceFilters[s.key]);
    return chOff || srcOff;
  }, [proposalChannelFilters, proposalSourceFilters]);

  const toggleProposalChannelFilter = (key) => {
    setProposalChannelFilters((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (!Object.values(next).some(Boolean)) return prev;
      localStorage.setItem('marketingKalenderProposalChannels', JSON.stringify(next));
      return next;
    });
  };

  const toggleProposalSourceFilter = (key) => {
    setProposalSourceFilters((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (!Object.values(next).some(Boolean)) return prev;
      localStorage.setItem('marketingKalenderProposalSources', JSON.stringify(next));
      return next;
    });
  };

  const resetProposalFilters = () => {
    setProposalChannelFilters({ ...DEFAULT_PROPOSAL_CHANNEL_FILTERS });
    setProposalSourceFilters({ ...DEFAULT_PROPOSAL_SOURCE_FILTERS });
    localStorage.removeItem('marketingKalenderProposalChannels');
    localStorage.removeItem('marketingKalenderProposalSources');
  };

  useEffect(() => {
    if (
      selectedProposal &&
      !proposalsFiltered.some((p) => p.id === selectedProposal.id)
    ) {
      setSelectedProposal(null);
    }
  }, [proposalsFiltered, selectedProposal]);

  const load = useCallback(async () => {
    setLoading(true);
    const timeoutMs = 90000;
    const withTimeout = (promise, label) =>
      Promise.race([
        promise,
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error(`${label} duurt te lang (server/database busy)`)), timeoutMs);
        }),
      ]);
    try {
      const [pRes, eRes] = await Promise.all([
        withTimeout(api.get('/marketing/proposals', { params: { status: 'new' } }), 'Voorstellen'),
        withTimeout(api.get('/marketing/events', { params: range }), 'Kalender'),
      ]);
      setProposals(pRes.data || []);
      setEvents(eRes.data || []);
    } catch (e) {
      console.error(e);
      alert('Laden mislukt: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const loadIgConfig = useCallback(async () => {
    try {
      const res = await api.get('/marketing/instagram/config');
      setIgConfig(res.data);
    } catch {
      setIgConfig(null);
    }
  }, []);

  useEffect(() => {
    loadIgConfig();
  }, [loadIgConfig]);

  const loadIgAutoConfig = useCallback(async () => {
    try {
      const res = await api.get('/marketing/instagram/auto-config');
      setIgAutoForm({
        enabled: Boolean(res.data?.enabled),
        default_publish_time: res.data?.default_publish_time || '10:00',
        max_posts_per_day: res.data?.max_posts_per_day ?? 2,
        timezone: res.data?.timezone || 'Europe/Amsterdam',
      });
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadIgAutoConfig();
  }, [loadIgAutoConfig]);

  const publishEventToIg = async (eventId) => {
    if (!eventIgImage.trim()) {
      alert('Vul een publieke https-afbeelding-URL in.');
      return;
    }
    setIgBusy(true);
    try {
      const res =       await api.post(`/marketing/events/${eventId}/publish-instagram`, {
        image_url: eventIgImage.trim() || undefined,
      });
      alert(`Geplaatst op Instagram (media id: ${res.data.media_id || 'ok'})`);
      const detail = await api.get(`/marketing/events/${eventId}`);
      setSelectedEvent(detail.data);
    } catch (e) {
      const err = e.response?.data?.error;
      alert(typeof err === 'string' ? err : err?.message || e.message);
    } finally {
      setIgBusy(false);
    }
  };

  const openEvent = async (ev) => {
    setSelectedProposal(null);
    try {
      const res = await api.get(`/marketing/events/${ev.id}`);
      setSelectedEvent(res.data);
      setEventNotes(res.data.notes || '');
      setEventIgImage(res.data.instagram_image_url || '');
      setEventIgImageMeta('');
      setEventIgAuto(Boolean(res.data.instagram_auto_publish));
      setEventIgPublishTime(res.data.instagram_publish_time || '');
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const saveEventInstagramSchedule = async (eventId, extra = {}) => {
    await api.patch(`/marketing/events/${eventId}`, {
      instagram_auto_publish: extra.instagram_auto_publish ?? eventIgAuto,
      instagram_publish_time: eventIgPublishTime.trim() || null,
      ...extra,
    });
    const detail = await api.get(`/marketing/events/${eventId}`);
    setSelectedEvent(detail.data);
  };

  const approveEventForInstagram = async (eventId) => {
    setIgBusy(true);
    try {
      await saveEventInstagramSchedule(eventId, {
        status: 'approved',
        instagram_auto_publish: true,
        instagram_publish_error: null,
      });
      setEventIgAuto(true);
      alert('Goedgekeurd voor automatische Instagram-post op de startdatum.');
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setIgBusy(false);
    }
  };

  const resolveEventSfeerImage = async (eventId) => {
    setIgBusy(true);
    try {
      const res = await api.post(`/marketing/events/${eventId}/resolve-sfeer-image`);
      setEventIgImage(res.data.image_url || '');
      setEventIgImageMeta(
        [
          res.data.gallery_reason,
          res.data.gallery_index != null ? `gallery #${res.data.gallery_index + 1}` : null,
          res.data.ean ? `EAN ${res.data.ean}` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      );
      setSelectedEvent((prev) =>
        prev?.id === eventId ? { ...prev, instagram_image_url: res.data.image_url } : prev
      );
    } catch (e) {
      const err = e.response?.data?.error;
      alert(typeof err === 'string' ? err : err?.message || e.message);
    } finally {
      setIgBusy(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/marketing/proposals/generate');
      await load();
      const b = res.data?.breakdown || {};
      const detail = [
        b.editorial != null ? `redactie ${b.editorial}` : null,
        b.calendar != null ? `kalender ${b.calendar}` : null,
        b.stock != null ? `voorraad ${b.stock}` : null,
        b.sales != null ? `verkoop ${b.sales}` : null,
        b.season != null ? `seizoen ${b.season}` : null,
      ]
        .filter(Boolean)
        .join(', ');
      const horizon = res.data?.horizon_days ?? 28;
      alert(
        `Klaar — ${res.data?.created ?? 0} nieuwe voorstellen (${horizon} dagen vooruit).\n${detail || ''}`
      );
      window.dispatchEvent(new Event('marketing-stats-refresh'));
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDismiss = async (id) => {
    setProposals((prev) => prev.filter((p) => p.id !== id));
    if (selectedProposal?.id === id) setSelectedProposal(null);
    try {
      await api.patch(`/marketing/proposals/${id}`, { status: 'dismissed' });
      window.dispatchEvent(new Event('marketing-stats-refresh'));
    } catch (e) {
      await load();
      alert(e.response?.data?.error || e.message || 'Verbergen mislukt');
    }
  };

  const handlePlan = async (id) => {
    const date = planDate || selectedProposal?.suggested_date;
    if (!date) {
      alert('Kies een datum');
      return;
    }
    await api.post(`/marketing/proposals/${id}/plan`, {
      starts_on: date,
      instagram_auto_publish: planIgAuto,
      approve_instagram: planIgAuto && igAutoForm.enabled,
    });
    setSelectedProposal(null);
    await load();
    window.dispatchEvent(new Event('marketing-stats-refresh'));
  };

  const handleProposalAi = async (id) => {
    setAiLoading(true);
    try {
      const res = await api.post(`/marketing/proposals/${id}/ai-copy`);
      setSelectedProposal((prev) => (prev?.id === id ? { ...prev, ai_copy_json: res.data.copy } : prev));
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleEventAi = async (id) => {
    setAiLoading(true);
    try {
      const res = await api.post(`/marketing/events/${id}/ai-copy`);
      setSelectedEvent((prev) => (prev?.id === id ? { ...prev, ai_copy_json: res.data.copy } : prev));
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setAiLoading(false);
    }
  };

  const saveEventNotes = async () => {
    if (!selectedEvent?.id) return;
    setSavingNotes(true);
    try {
      await api.patch(`/marketing/events/${selectedEvent.id}`, { notes: eventNotes });
      setSelectedEvent((prev) => ({ ...prev, notes: eventNotes }));
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setSavingNotes(false);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Campagne uit kalender verwijderen?')) return;
    await api.delete(`/marketing/events/${id}`);
    setSelectedEvent(null);
    await load();
  };

  const moveEventToDay = async (eventId, fromIso, toIso) => {
    const ev = events.find((e) => e.id === eventId);
    if (!ev) return;
    const dates = shiftEventDates(ev, fromIso, toIso);
    if (!dates) return;

    setMovingEventId(eventId);
    const prevEvents = events;
    setEvents((list) => list.map((e) => (e.id === eventId ? { ...e, ...dates } : e)));
    if (selectedEvent?.id === eventId) {
      setSelectedEvent((prev) => (prev ? { ...prev, ...dates } : prev));
    }

    try {
      await api.patch(`/marketing/events/${eventId}`, dates);
    } catch (e) {
      setEvents(prevEvents);
      alert(e.response?.data?.error || e.message);
      await load();
    } finally {
      setMovingEventId(null);
    }
  };

  const grid = useMemo(() => {
    const firstDow = (monthStart(cursor).getDay() + 6) % 7;
    const total = daysInMonth(cursor);
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let day = 1; day <= total; day++) {
      const iso = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayEvents = events.filter((ev) => eventOnDay(ev, iso));
      cells.push({ day, iso, events: dayEvents });
    }
    return cells;
  }, [cursor, events]);

  const monthLabel = cursor.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });

  return (
    <div className="marketing-kalender-page">
      <header className="marketing-kalender-header">
        <div>
          <h1>
            <FiCalendar aria-hidden /> Marketingkalender
          </h1>
          <p className="muted">
            Campagnes voor <strong>4shine.nl</strong> (WooCommerce), Meta en e-mail. AI werkt als senior marketingmanager voor
            4Shine — plan voorstellen in de kalender en klik ingeplande items voor details en teksten.{' '}
            <Link to="/marketing/instagram">Instagram postoverzicht &amp; instellingen →</Link>
          </p>
        </div>
        <div className="marketing-kalender-actions">
          <button type="button" className="btn btn-secondary" onClick={load} disabled={loading}>
            <FiRefreshCw /> Vernieuwen
          </button>
          <button type="button" className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
            {generating ? 'Bezig…' : 'Voorstellen vernieuwen'}
          </button>
        </div>
      </header>

      <div className="marketing-kalender-layout">
        <aside className="marketing-inbox">
          {selectedEvent ? (
            <div className="marketing-detail-panel marketing-event-panel">
              <button type="button" className="marketing-back-link" onClick={() => setSelectedEvent(null)}>
                ← Terug naar voorstellen
              </button>
              <span className={`marketing-chip marketing-chip-${selectedEvent.channel}`}>
                {CHANNEL_LABELS[selectedEvent.channel] || selectedEvent.channel}
              </span>
              <h2>{selectedEvent.title}</h2>
              <p className="marketing-event-dates">
                {selectedEvent.starts_on}
                {selectedEvent.ends_on ? ` t/m ${selectedEvent.ends_on}` : ''}
                {selectedEvent.status === 'approved' && (
                  <span className="marketing-chip marketing-chip-approved"> IG goedgekeurd</span>
                )}
                {selectedEvent.instagram_auto_publish && selectedEvent.status !== 'approved' && (
                  <span className="muted"> · auto-IG (nog niet goedgekeurd)</span>
                )}
              </p>
              {selectedEvent.instagram_publish_error && (
                <p className="marketing-ig-error">Laatste auto-fout: {selectedEvent.instagram_publish_error}</p>
              )}
              {selectedEvent.description && <p>{selectedEvent.description}</p>}
              {selectedEvent.proposal_source && (
                <p className="muted">
                  Bron: {SOURCE_LABELS[selectedEvent.proposal_source] || selectedEvent.proposal_source}
                  {selectedEvent.proposal_meta?.event_date
                    ? ` · piek rond ${selectedEvent.proposal_meta.event_date}`
                    : ''}
                </p>
              )}
              {selectedEvent.product_naam && (
                <p className="muted">
                  Product: {selectedEvent.product_naam}
                  {selectedEvent.product_sku ? ` (${selectedEvent.product_sku})` : ''}
                  {selectedEvent.product_ean ? ` · EAN ${selectedEvent.product_ean}` : ''}
                </p>
              )}
              <label className="marketing-plan-label">
                Notities
                <textarea
                  className="form-input marketing-notes-input"
                  rows={3}
                  value={eventNotes}
                  onChange={(e) => setEventNotes(e.target.value)}
                  placeholder="Eigen ideeën, kortingen, doelgroep…"
                />
              </label>
              <div className="marketing-detail-actions">
                <button type="button" className="btn btn-secondary" onClick={saveEventNotes} disabled={savingNotes}>
                  {savingNotes ? 'Opslaan…' : 'Notities opslaan'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleEventAi(selectedEvent.id)}
                  disabled={aiLoading}
                >
                  <FiZap /> {aiLoading ? 'AI…' : 'AI-teksten & ideeën'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => deleteEvent(selectedEvent.id)}>
                  <FiTrash2 /> Verwijderen
                </button>
              </div>
              {(selectedEvent.channel === 'meta' ||
                selectedEvent.channel === 'woo' ||
                selectedEvent.product_id ||
                selectedEvent.product_ean) && (
                <div className="marketing-ig-publish-block">
                  <h4>Instagram post</h4>
                  {selectedEvent.channel === 'email' && (
                    <p className="muted">
                      Dit voorstel staat als e-mail gepland; je kunt dezelfde campagne ook op Instagram zetten.
                    </p>
                  )}
                  <div className="marketing-ig-schedule-row">
                    <label className="marketing-ig-checkbox">
                      <input
                        type="checkbox"
                        checked={eventIgAuto}
                        onChange={(e) => setEventIgAuto(e.target.checked)}
                      />
                      Automatisch posten op startdatum
                    </label>
                    <label>
                      Tijd (optioneel)
                      <input
                        className="form-input marketing-ig-time-input"
                        value={eventIgPublishTime}
                        onChange={(e) => setEventIgPublishTime(e.target.value)}
                        placeholder={igAutoForm.default_publish_time}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={igBusy}
                      onClick={async () => {
                        setIgBusy(true);
                        try {
                          await saveEventInstagramSchedule(selectedEvent.id);
                        } catch (e) {
                          alert(e.response?.data?.error || e.message);
                        } finally {
                          setIgBusy(false);
                        }
                      }}
                    >
                      Planning opslaan
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={igBusy || !igAutoForm.enabled}
                      onClick={() => approveEventForInstagram(selectedEvent.id)}
                    >
                      Goedkeuren voor auto-post
                    </button>
                  </div>
                  {!igAutoForm.enabled && (
                    <p className="muted">
                      Zet automatisering aan onder <Link to="/marketing/instagram">Instagram → Instellingen</Link>.
                    </p>
                  )}
                  {selectedEvent.instagram_post_id && (
                    <p className="muted">
                      Geplaatst: {selectedEvent.instagram_posted_at || '—'} (id {selectedEvent.instagram_post_id})
                    </p>
                  )}
                  <label className="marketing-plan-label">
                    Afbeelding URL (https)
                    <input
                      className="form-input"
                      value={eventIgImage}
                      onChange={(e) => setEventIgImage(e.target.value)}
                      placeholder="Sfeerfoto ophalen of handmatige URL"
                    />
                  </label>
                  {eventIgImageMeta && <p className="muted">{eventIgImageMeta}</p>}
                  {selectedEvent.instagram_image_resolved_at && !eventIgImageMeta && (
                    <p className="muted">
                      Opgeslagen sfeer-URL ({selectedEvent.instagram_image_resolved_at.slice(0, 10)})
                    </p>
                  )}
                  <div className="marketing-detail-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={igBusy || (!selectedEvent.product_id && !selectedEvent.product_ean)}
                      onClick={() => resolveEventSfeerImage(selectedEvent.id)}
                    >
                      Sfeerfoto ophalen (Woo / EAN)
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={igBusy || !igConfig?.configured}
                      onClick={() => publishEventToIg(selectedEvent.id)}
                    >
                      Post naar Instagram
                    </button>
                  </div>
                  {!igConfig?.configured && (
                    <p className="muted">
                      Koppel eerst Instagram onder <Link to="/marketing/instagram">Instagram → Instellingen</Link>.
                    </p>
                  )}
                </div>
              )}
              <AiCopyBlock copy={selectedEvent.ai_copy_json} />
            </div>
          ) : (
            <>
              <h2>
                Nieuwe voorstellen ({proposalsFiltered.length}
                {proposalFiltersActive && proposals.length !== proposalsFiltered.length
                  ? ` / ${proposals.length}`
                  : ''}
                )
              </h2>
              <div className="marketing-proposal-filters" role="group" aria-label="Filter voorstellen">
                <div className="marketing-proposal-filter-row">
                  <span className="marketing-proposal-filter-label">Kanaal</span>
                  <div className="marketing-proposal-filter-chips">
                    {PROPOSAL_FILTER_CHANNELS.map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        className={`marketing-proposal-filter-chip marketing-proposal-filter-chip-${key}${
                          proposalChannelFilters[key] ? ' is-on' : ''
                        }`}
                        aria-pressed={Boolean(proposalChannelFilters[key])}
                        onClick={() => toggleProposalChannelFilter(key)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="marketing-proposal-filter-row">
                  <span className="marketing-proposal-filter-label">Bron</span>
                  <div className="marketing-proposal-filter-chips">
                    {PROPOSAL_FILTER_SOURCES.map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        className={`marketing-proposal-filter-chip${
                          proposalSourceFilters[key] ? ' is-on' : ''
                        }`}
                        aria-pressed={Boolean(proposalSourceFilters[key])}
                        onClick={() => toggleProposalSourceFilter(key)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {proposalFiltersActive && (
                  <button type="button" className="marketing-proposal-filter-reset" onClick={resetProposalFilters}>
                    Toon alles
                  </button>
                )}
              </div>
              <p className="muted marketing-proposal-hint">
                Redactieplan: ca. 2× Meta per dag, plus e-mail en Woo, gespreid over ~4 weken — gesorteerd op datum.
                Alleen producten met voorraad (geen losse fittingen); recent gepromoot (~3 weken) en off-season worden overgeslagen.
                Klik × naast een voorstel om het te verbergen
                (komt niet terug bij vernieuwen).
              </p>
              {loading ? (
                <p className="muted">Laden…</p>
              ) : proposals.length === 0 ? (
                <p className="muted">Geen open voorstellen. Klik op &quot;Voorstellen vernieuwen&quot;.</p>
              ) : proposalsFiltered.length === 0 ? (
                <p className="muted">
                  Geen voorstellen voor deze filters. Zet een kanaal of bron aan, of klik &quot;Toon alles&quot;.
                </p>
              ) : (
                <ul className="marketing-proposal-list">
                  {proposalDateGroups.map((group) => (
                    <li key={group.date} className="marketing-proposal-day-group">
                      <h3 className="marketing-proposal-day-heading">
                        {group.date}
                        <span className="muted"> ({group.items.length})</span>
                      </h3>
                      <ul className="marketing-proposal-day-items">
                        {group.items.map((p) => (
                          <li key={p.id} className="marketing-proposal-row">
                            <button
                              type="button"
                              className={`marketing-proposal-card ${selectedProposal?.id === p.id ? 'active' : ''}`}
                              onClick={() => {
                                setSelectedProposal(p);
                                const today = new Date().toISOString().slice(0, 10);
                                const sd = p.suggested_date;
                                setPlanDate(sd && sd >= today ? sd : today);
                                setPlanIgAuto(p.channel === 'meta');
                              }}
                            >
                              <strong>{p.title}</strong>
                              <span className="marketing-proposal-meta">
                                <span className={`marketing-chip marketing-chip-${p.channel}`}>
                                  {CHANNEL_LABELS[p.channel] || p.channel}
                                </span>
                                {' · '}
                                {SOURCE_LABELS[p.source] || p.source}
                              </span>
                            </button>
                            <button
                              type="button"
                              className="marketing-proposal-dismiss"
                              title="Niet interessant — verbergen"
                              aria-label={`Voorstel verbergen: ${p.title}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDismiss(p.id);
                              }}
                            >
                              <FiX />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}

              {selectedProposal && proposalUsesIgComposer(selectedProposal) ? (
                <MarketingProposalComposer
                  proposal={selectedProposal}
                  accountLabel={igConfig?.label || '4shine'}
                  igAutoEnabled={Boolean(igAutoForm.enabled)}
                  defaultPublishTime={igAutoForm.default_publish_time}
                  onPlanned={async () => {
                    setSelectedProposal(null);
                    await load();
                    window.dispatchEvent(new Event('marketing-stats-refresh'));
                  }}
                  onDismiss={handleDismiss}
                />
              ) : (
                selectedProposal && (
                  <div className="marketing-detail-panel">
                    <h3>{selectedProposal.title}</h3>
                    <p className="muted marketing-internal-briefing">{selectedProposal.description}</p>
                    {selectedProposal.product_naam && (
                      <p className="muted">
                        Product: {selectedProposal.product_naam}{' '}
                        {selectedProposal.product_sku ? `(${selectedProposal.product_sku})` : ''}
                      </p>
                    )}
                    <label className="marketing-plan-label">
                      Plan op datum
                      <input
                        type="date"
                        className="form-input"
                        value={planDate}
                        onChange={(e) => setPlanDate(e.target.value)}
                      />
                    </label>
                    <div className="marketing-detail-actions">
                      <label className="marketing-ig-checkbox marketing-plan-ig-auto">
                        <input
                          type="checkbox"
                          checked={planIgAuto}
                          onChange={(e) => setPlanIgAuto(e.target.checked)}
                        />
                        Ook automatische Instagram-post op deze datum
                      </label>
                      <button type="button" className="btn btn-primary" onClick={() => handlePlan(selectedProposal.id)}>
                        <FiCheck /> Inplannen
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => handleProposalAi(selectedProposal.id)}
                        disabled={aiLoading}
                      >
                        <FiZap /> {aiLoading ? 'AI…' : 'AI-teksten'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => handleDismiss(selectedProposal.id)}
                      >
                        <FiX /> Verbergen
                      </button>
                    </div>
                    <AiCopyBlock copy={selectedProposal.ai_copy_json} />
                  </div>
                )
              )}
            </>
          )}
        </aside>

        <section className="marketing-calendar">
          <div className="marketing-calendar-toolbar">
            <button type="button" className="btn-icon" onClick={() => setCursor(addMonths(cursor, -1))} aria-label="Vorige maand">
              <FiChevronLeft />
            </button>
            <h2>{monthLabel}</h2>
            <button type="button" className="btn-icon" onClick={() => setCursor(addMonths(cursor, 1))} aria-label="Volgende maand">
              <FiChevronRight />
            </button>
          </div>
          <p className="muted marketing-calendar-hint">
            Sleep campagnes naar een andere dag om te verplaatsen. Klik voor details.
          </p>
          <div className="marketing-calendar-scroll">
            <div className="marketing-calendar-inner">
              <div className="marketing-calendar-weekdays">
                {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </div>
              <div className="marketing-calendar-grid">
                {grid.map((cell, idx) =>
                  cell ? (
                    <div
                      key={cell.iso}
                      className={`marketing-calendar-day${
                        dropHighlightIso === cell.iso ? ' marketing-calendar-day-drop-target' : ''
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        setDropHighlightIso(cell.iso);
                      }}
                      onDragLeave={(e) => {
                        if (e.currentTarget.contains(e.relatedTarget)) return;
                        setDropHighlightIso((cur) => (cur === cell.iso ? null : cur));
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDropHighlightIso(null);
                        const raw = e.dataTransfer.getData('text/plain');
                        const eventId = parseInt(raw, 10);
                        const fromIso = dragRef.current.fromIso;
                        dragRef.current.didMove = true;
                        if (eventId && fromIso) {
                          moveEventToDay(eventId, fromIso, cell.iso);
                        }
                      }}
                    >
                      <span className="marketing-calendar-daynum">{cell.day}</span>
                      <div className="marketing-calendar-day-events">
                        {cell.events.map((ev) => (
                          <button
                            key={`${ev.id}-${cell.iso}`}
                            type="button"
                            draggable
                            className={`marketing-event-pill marketing-event-${ev.channel} ${
                              selectedEvent?.id === ev.id ? 'selected' : ''
                            }${movingEventId === ev.id ? ' marketing-event-pill-moving' : ''}`}
                            title={`${ev.title} — slepen om te verplaatsen`}
                            onDragStart={(e) => {
                              dragRef.current = { eventId: ev.id, fromIso: cell.iso, didMove: false };
                              e.dataTransfer.setData('text/plain', String(ev.id));
                              e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragEnd={() => {
                              setDropHighlightIso(null);
                              window.setTimeout(() => {
                                dragRef.current = { eventId: null, fromIso: null, didMove: false };
                              }, 100);
                            }}
                            onClick={() => {
                              if (dragRef.current.didMove) return;
                              openEvent(ev);
                            }}
                          >
                            {ev.title}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div key={`empty-${idx}`} className="marketing-calendar-day empty" />
                  )
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MarketingKalender;
